import React, { useEffect, useState, useCallback, useRef } from 'react'
  import {
  MapContainer, TileLayer, Polygon, CircleMarker,
  Popup, useMap, useMapEvents,
} from 'react-leaflet'
import 'leaflet/dist/leaflet.css'
import Wrapper from '../../components/Wrapper'
import axiosInstance from '../../utils/axios'

// ─── Constants ────────────────────────────────────────────────────────────────

const COLORS = [
  '#e74c3c', '#2ecc71', '#3498db', '#f39c12',
  '#9b59b6', '#1abc9c', '#e67e22', '#e91e63',
]
const INDIA_CENTER = [20.5937, 78.9629]
const POLYGON_ZOOM_THRESHOLD = 13

// Free tile providers — no API key needed
const TILE_LAYERS = {
  roadmap:   'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
  satellite: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
}
const TILE_ATTRIBUTION = {
  roadmap:   '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
  satellite: 'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, AEX, GeoEye',
}

// ─── Pure helpers ─────────────────────────────────────────────────────────────

function toLatLng(p) {
  return { lat: p.latitude ?? p.lat, lng: p.longitude ?? p.lng }
}

function polyCenter(points) {
  const lls = points.map(toLatLng)
  return [
    lls.reduce((s, p) => s + p.lat, 0) / lls.length,
    lls.reduce((s, p) => s + p.lng, 0) / lls.length,
  ]
}

// ─── Inner map helpers (must live inside MapContainer) ────────────────────────

function FitBounds({ farms }) {
  const map = useMap()
  useEffect(() => {
    if (!farms.length) return
    const allPoints = farms.flatMap(f => f.geo_location.data.map(toLatLng))
    if (!allPoints.length) return
    const lats = allPoints.map(p => p.lat)
    const lngs = allPoints.map(p => p.lng)
    map.fitBounds(
      [[Math.min(...lats), Math.min(...lngs)], [Math.max(...lats), Math.max(...lngs)]],
      { padding: [40, 40] }
    )
  }, [farms, map])
  return null
}

function ZoomTracker({ onZoom }) {
  useMapEvents({ zoomend: (e) => onZoom(e.target.getZoom()) })
  return null
}

// ─── UI sub-components ────────────────────────────────────────────────────────

function StatCard({ label, value, accent }) {
  return (
    <div style={{
      flex: 1,
      minWidth: 0,
      background: '#fff',
      border: '1px solid #e5e7eb',
      borderTop: `3px solid ${accent}`,
      borderRadius: 10,
      padding: '12px 16px',
    }}>
      <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ fontSize: 24, fontWeight: 700, color: '#111827', lineHeight: 1 }}>
        {value}
      </div>
    </div>
  )
}

function SearchInput({ value, onChange }) {
  const [focused, setFocused] = useState(false)
  return (
    <div style={{ position: 'relative', width: '100%', maxWidth: 300 }}>
      <svg
        style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
        width="15" height="15" viewBox="0 0 24 24" fill="none"
        stroke={focused ? '#16a34a' : '#9ca3af'} strokeWidth="2.2"
        strokeLinecap="round" strokeLinejoin="round"
      >
        <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
      </svg>
      <input
        type="text"
        value={value}
        onChange={onChange}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder="Search farmer or farm name…"
        style={{
          width: '100%',
          paddingLeft: 36,
          paddingRight: 14,
          paddingTop: 9,
          paddingBottom: 9,
          fontSize: 13,
          border: `1.5px solid ${focused ? '#16a34a' : '#e5e7eb'}`,
          borderRadius: 24,
          outline: 'none',
          background: '#fff',
          color: '#374151',
          boxSizing: 'border-box',
          transition: 'border-color 0.15s',
          boxShadow: focused ? '0 0 0 3px rgba(22,163,74,0.1)' : 'none',
        }}
      />
    </div>
  )
}

function MapTypeToggle({ mapType, onChange }) {
  const types = [
    { id: 'roadmap', label: 'Map' },
    { id: 'satellite', label: 'Satellite' },
  ]
  return (
    <div style={{
      display: 'flex',
      borderRadius: 8,
      overflow: 'hidden',
      boxShadow: '0 1px 8px rgba(0,0,0,0.2)',
      background: '#fff',
    }}>
      {types.map((t, i) => (
        <button
          key={t.id}
          onClick={() => onChange(t.id)}
          style={{
            padding: '7px 13px',
            fontSize: 12,
            fontWeight: 600,
            border: 'none',
            borderLeft: i > 0 ? '1px solid #e5e7eb' : 'none',
            cursor: 'pointer',
            background: mapType === t.id ? '#111827' : '#fff',
            color: mapType === t.id ? '#fff' : '#4b5563',
            transition: 'background 0.15s, color 0.15s',
            lineHeight: 1,
          }}
        >
          {t.label}
        </button>
      ))}
    </div>
  )
}

function FullscreenButton({ isFullscreen, onClick }) {
  return (
    <button
      onClick={onClick}
      title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
      style={{
        width: 34, height: 34,
        borderRadius: 8,
        border: 'none',
        background: '#fff',
        boxShadow: '0 1px 8px rgba(0,0,0,0.2)',
        cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#374151',
      }}
    >
      {isFullscreen ? (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3" />
        </svg>
      ) : (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
        </svg>
      )}
    </button>
  )
}

function Legend({ farmers }) {
  if (farmers.length === 0) return null
  return (
    <div style={{
      position: 'absolute',
      bottom: 28,
      left: 12,
      background: '#fff',
      border: '1px solid #e5e7eb',
      borderRadius: 12,
      padding: '12px 15px',
      zIndex: 1000,
      maxHeight: 200,
      overflowY: 'auto',
      boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
      minWidth: 160,
    }}>
      <div style={{
        fontSize: 10, fontWeight: 700, letterSpacing: 1.2, color: '#9ca3af',
        textTransform: 'uppercase', marginBottom: 10,
      }}>
        Farmers
      </div>
      {farmers.map(f => (
        <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 7 }}>
          <span style={{
            width: 10, height: 10, borderRadius: 3,
            background: f.color, flexShrink: 0, display: 'inline-block',
          }} />
          <span style={{ fontSize: 13, color: '#374151', lineHeight: 1.3 }}>{f.name}</span>
        </div>
      ))}
    </div>
  )
}

function LoadingOverlay() {
  return (
    <>
      <style>{`@keyframes _spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', flex: 1, gap: 14,
        background: '#f9fafb', borderRadius: 12, border: '1px solid #e5e7eb',
      }}>
        <div style={{
          width: 40, height: 40,
          border: '3px solid #dcfce7',
          borderTopColor: '#16a34a',
          borderRadius: '50%',
          animation: '_spin 0.75s linear infinite',
        }} />
        <span style={{ fontSize: 14, color: '#6b7280', fontWeight: 500 }}>
          Loading farm boundaries…
        </span>
      </div>
    </>
  )
}

function EmptyState() {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', flex: 1, gap: 14, padding: 40,
      background: '#f9fafb', borderRadius: 12, border: '1px solid #e5e7eb',
    }}>
      <svg width="60" height="60" viewBox="0 0 24 24" fill="none">
        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"
          fill="#dcfce7" stroke="#16a34a" strokeWidth="1.5" />
        <circle cx="12" cy="9" r="2.5" fill="#16a34a" />
      </svg>
      <div>
        <div style={{ fontSize: 17, fontWeight: 600, color: '#374151', textAlign: 'center', marginBottom: 6 }}>
          No farm boundaries found
        </div>
        <div style={{ fontSize: 13, color: '#9ca3af', textAlign: 'center', maxWidth: 300, lineHeight: 1.5 }}>
          Farm boundaries will appear here once field officers add geofence data.
        </div>
      </div>
    </div>
  )
}

function PopupContent({ farm }) {
  return (
    <div style={{ minWidth: 180, fontFamily: 'inherit' }}>
      <div style={{ fontWeight: 700, fontSize: 14, color: '#111827', marginBottom: 3 }}>
        {farm.farm_name}
      </div>
      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 10 }}>
        {farm.farmerName}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{
          fontSize: 11, fontWeight: 600, color: '#15803d',
          background: '#dcfce7', borderRadius: 4, padding: '2px 8px',
        }}>
          ✓ Boundary Available
        </span>
        {farm.farm_size && (
          <span style={{ fontSize: 11, color: '#6b7280' }}>
            {Number(farm.farm_size).toFixed(1)} {farm.farm_size_unit || ''}
          </span>
        )}
      </div>
    </div>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function AllFarmsMap() {
  const containerRef = useRef(null)

  const [farms,        setFarms]        = useState([])
  const [loading,      setLoading]      = useState(true)
  const [error,        setError]        = useState(null)
  const [search,       setSearch]       = useState('')
  const [mapType,      setMapType]      = useState('roadmap')
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [zoom,         setZoom]         = useState(5)

  // ─── Data fetching ────────────────────────────────────────────────────────

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true)
        setError(null)

        const resp = await axiosInstance.get('/super-admin/all-farms')
        const rawFarms = resp.data.farms || []

        const colorMap = {}
        let colorIndex = 0
        const allFarms = rawFarms.map(farm => {
          if (!colorMap[farm.owned_by]) {
            colorMap[farm.owned_by] = COLORS[colorIndex % COLORS.length]
            colorIndex++
          }
          return {
            ...farm,
            farmerName: farm.farmer_name || farm.owned_by,
            farmerId:   farm.owned_by,
            color:      colorMap[farm.owned_by],
          }
        })

        setFarms(allFarms)
      } catch (err) {
        console.error('AllFarmsMap load error:', err)
        setError('Failed to load farms. Please refresh and try again.')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  // ─── Fullscreen ───────────────────────────────────────────────────────────

  const handleFullscreen = useCallback(() => {
    if (!containerRef.current) return
    if (!document.fullscreenElement) containerRef.current.requestFullscreen?.()
    else document.exitFullscreen?.()
  }, [])

  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement)
    document.addEventListener('fullscreenchange', onFsChange)
    return () => document.removeEventListener('fullscreenchange', onFsChange)
  }, [])

  // ─── Derived state ────────────────────────────────────────────────────────

  const searchTerm = search.trim().toLowerCase()

  const farmMatches = useCallback(
    (f) =>
      !searchTerm ||
      f.farmerName.toLowerCase().includes(searchTerm) ||
      f.farm_name.toLowerCase().includes(searchTerm),
    [searchTerm]
  )

  const legendFarmers = [
    ...new Map(
      farms
        .filter(farmMatches)
        .map(f => [f.farmerId, { name: f.farmerName, color: f.color }])
    ).entries(),
  ].map(([id, v]) => ({ id, ...v }))

  const uniqueFarmersCount = new Set(farms.map(f => f.farmerId)).size
  const showMap = !loading && farms.length > 0

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <Wrapper>
      <style>{`.leaflet-container { font-family: inherit; }`}</style>
      <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 60px)', paddingBottom: 12 }}>

        {/* ── Header ── */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: 12, padding: '16px 0 14px', flexShrink: 0,
        }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#111827', lineHeight: 1.2 }}>
              Farm Map
            </h1>
            <p style={{ margin: '3px 0 0', fontSize: 13, color: '#6b7280' }}>
              {loading
                ? 'Loading farm boundaries…'
                : `${farms.length} farm${farms.length !== 1 ? 's' : ''} on map`}
            </p>
          </div>
          <SearchInput value={search} onChange={e => setSearch(e.target.value)} />
        </div>

        {/* ── Stats ── */}
        {!loading && (
          <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexShrink: 0 }}>
            <StatCard label="Farmers on Map" value={uniqueFarmersCount} accent="#2563eb" />
          </div>
        )}

        {/* ── Error banner ── */}
        {error && (
          <div style={{
            padding: '10px 14px', background: '#fef2f2',
            border: '1px solid #fecaca', borderRadius: 8,
            color: '#b91c1c', fontSize: 13, marginBottom: 12, flexShrink: 0,
          }}>
            {error}
          </div>
        )}

        {/* ── Main content ── */}
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>

          {loading && <LoadingOverlay />}

          {!loading && farms.length === 0 && !error && <EmptyState />}

          {/* Map card */}
          {showMap && (
            <div
              ref={containerRef}
              style={{
                flex: 1,
                minHeight: 0,
                position: 'relative',
                borderRadius: 12,
                overflow: 'hidden',
                boxShadow: '0 2px 16px rgba(0,0,0,0.09)',
                border: '1px solid #e5e7eb',
              }}
            >
              <MapContainer
                center={INDIA_CENTER}
                zoom={5}
                style={{ width: '100%', height: '100%' }}
                zoomControl
                attributionControl
              >
                {/* Tile layer — swaps when toggle changes */}
                <TileLayer
                  key={mapType}
                  url={TILE_LAYERS[mapType]}
                  attribution={TILE_ATTRIBUTION[mapType]}
                  maxZoom={19}
                />

                {/* Auto fit-bounds once farms load */}
                <FitBounds farms={farms} />

                {/* Track zoom for dot vs polygon switching */}
                <ZoomTracker onZoom={setZoom} />

                {farms.map(farm => {
                  const isMatch = farmMatches(farm)
                  const center  = polyCenter(farm.geo_location.data)

                  if (zoom < POLYGON_ZOOM_THRESHOLD) {
                    return (
                      <CircleMarker
                        key={farm.farm_id}
                        center={center}
                        radius={isMatch ? 10 : 7}
                        pathOptions={{
                          color:       isMatch ? '#fff' : farm.color,
                          weight:      isMatch ? 2.5   : 1,
                          fillColor:   farm.color,
                          fillOpacity: isMatch ? 0.92  : 0.25,
                          opacity:     1,
                        }}
                        eventHandlers={{
                          click: () => isMatch && farm,
                        }}
                      >
                        {isMatch && (
                          <Popup>
                            <PopupContent farm={farm} />
                          </Popup>
                        )}
                      </CircleMarker>
                    )
                  }

                  // Polygon mode
                  const positions = farm.geo_location.data
                    .map(toLatLng)
                    .filter(p =>
                      typeof p.lat === 'number' && !isNaN(p.lat) &&
                      typeof p.lng === 'number' && !isNaN(p.lng)
                    )
                    .map(p => [p.lat, p.lng])

                  if (positions.length < 3) return null

                  return (
                    <Polygon
                      key={farm.farm_id}
                      positions={positions}
                      pathOptions={{
                        color:        farm.color,
                        weight:       isMatch && searchTerm ? 2.5 : isMatch ? 2 : 0.8,
                        opacity:      isMatch ? 1    : 0.18,
                        fillColor:    farm.color,
                        fillOpacity:  isMatch ? 0.3  : 0.05,
                      }}
                    >
                      {isMatch && (
                        <Popup>
                          <PopupContent farm={farm} />
                        </Popup>
                      )}
                    </Polygon>
                  )
                })}
              </MapContainer>

              {/* ── Overlays (sit above the Leaflet canvas) ── */}
              <div style={{ position: 'absolute', top: 12, left: 12, zIndex: 1000 }}>
                <MapTypeToggle mapType={mapType} onChange={setMapType} />
              </div>
              <div style={{ position: 'absolute', top: 12, right: 12, zIndex: 1000 }}>
                <FullscreenButton isFullscreen={isFullscreen} onClick={handleFullscreen} />
              </div>
              <Legend farmers={legendFarmers} />
            </div>
          )}
        </div>
      </div>
    </Wrapper>
  )
}
